/**
 * Pure helpers for proposal lists: labels, review links, the flags a row
 * shows, and grouping by target for the Review page.
 */
import type { ProposalDetail, ProposalKind, ProposalSummary } from '@/api/types'

export const KIND_LABELS: Record<ProposalKind, string> = {
  event_type_rules: 'Rule changes',
  event_type_settings: 'Settings changes',
  partial_content: 'Content change',
}

/** Kind filter options for the Review page, in display order. */
export const KIND_OPTIONS: readonly { value: ProposalKind; label: string }[] = [
  { value: 'event_type_rules', label: KIND_LABELS.event_type_rules },
  { value: 'event_type_settings', label: KIND_LABELS.event_type_settings },
  { value: 'partial_content', label: KIND_LABELS.partial_content },
]

export const isProposalKind = (v: unknown): v is ProposalKind =>
  v === 'event_type_rules' || v === 'event_type_settings' || v === 'partial_content'

type Linkable = Pick<ProposalSummary, 'id' | 'kind'> & { target: { id: string } }

/** Where "Review" goes: the object's editor with the proposal loaded. */
export type ProposalLink =
  | {
      to: '/event-types/$eventTypeId'
      params: { eventTypeId: string }
      search: { proposal: string; tab?: 'settings' }
    }
  | { to: '/partials/$partialId'; params: { partialId: string }; search: { proposal: string } }

export function proposalLink(p: Linkable): ProposalLink {
  if (p.kind === 'partial_content')
    return {
      to: '/partials/$partialId',
      params: { partialId: p.target.id },
      search: { proposal: p.id },
    }
  return {
    to: '/event-types/$eventTypeId',
    params: { eventTypeId: p.target.id },
    search:
      p.kind === 'event_type_settings' ? { proposal: p.id, tab: 'settings' } : { proposal: p.id },
  }
}

/** The first non-empty line of the notes. */
export function firstLine(notes: string): string {
  return (
    notes
      .split(/\r?\n/)
      .map((l) => l.trim())
      .find(Boolean) ?? ''
  )
}

export type FlagTone = 'warning' | 'danger' | 'neutral' | 'success' | 'outline'

export interface ProposalFlag {
  key: 'applied' | 'dismissed' | 'expired' | 'stale' | 'errors' | 'missing'
  label: string
  tone: FlagTone
  /** Longer text for a tooltip. */
  hint: string
}

type Flaggable = Pick<ProposalSummary, 'status' | 'expired' | 'stale' | 'check_error_count'> & {
  target: { exists: boolean }
}

/**
 * What a row should warn about. A closed proposal shows only its status;
 * an open one shows expiry, a deleted target, staleness, and check errors.
 */
export function proposalFlags(p: Flaggable): ProposalFlag[] {
  if (p.status === 'applied')
    return [{ key: 'applied', label: 'Applied', tone: 'success', hint: 'Saved by a person.' }]
  if (p.status === 'dismissed')
    return [
      { key: 'dismissed', label: 'Dismissed', tone: 'outline', hint: 'Closed without changes.' },
    ]
  if (p.expired)
    return [
      {
        key: 'expired',
        label: 'Expired',
        tone: 'outline',
        hint: 'Open proposals expire after 30 days and can’t be applied.',
      },
    ]
  const out: ProposalFlag[] = []
  if (!p.target.exists)
    out.push({
      key: 'missing',
      label: 'Target deleted',
      tone: 'danger',
      hint: 'What it changes no longer exists.',
    })
  else if (p.stale)
    out.push({
      key: 'stale',
      label: 'Out of date',
      tone: 'warning',
      hint: 'Someone saved a change after it was made. Saving it replaces that change.',
    })
  if (p.check_error_count > 0)
    out.push({
      key: 'errors',
      label: p.check_error_count === 1 ? '1 problem' : `${p.check_error_count} problems`,
      tone: 'danger',
      hint: 'It needs fixing before it can be saved.',
    })
  return out
}

/** A detail's flags (it has the full check errors rather than a count). */
export function detailFlags(p: ProposalDetail): ProposalFlag[] {
  return proposalFlags({ ...p, check_error_count: p.check_errors.length })
}

export interface ProposalGroup<T> {
  targetId: string
  target: T extends { target: infer U } ? U : never
  kind: 'event_type' | 'partial'
  proposals: T[]
}

/**
 * Group proposals by what they change, keeping newest first: groups are
 * ordered by their newest proposal, and proposals within a group newest
 * first. Rule and settings proposals for one event type share a group.
 */
export function groupByTarget<T extends Pick<ProposalSummary, 'kind' | 'created_at' | 'target'>>(
  items: readonly T[],
): ProposalGroup<T>[] {
  const sorted = [...items].sort((a, b) => b.created_at.localeCompare(a.created_at))
  const groups = new Map<string, ProposalGroup<T>>()
  for (const p of sorted) {
    let g = groups.get(p.target.id)
    if (!g) {
      g = {
        targetId: p.target.id,
        target: p.target as ProposalGroup<T>['target'],
        kind: p.kind === 'partial_content' ? 'partial' : 'event_type',
        proposals: [],
      }
      groups.set(p.target.id, g)
    }
    g.proposals.push(p)
  }
  return [...groups.values()]
}
